import { useContext, useMemo } from "react";
import { AuthContext } from "../context/AuthContext";
import { Layer, mockLayers } from "../global/constants/layers";


// Hook return interface
interface LayerPermissionsHook {
  canViewLayer: (layerTitle: string) => boolean;
  getAvailableLayers: () => Layer[];
  hasAdminAccess: boolean;
}

/**
 * Hook to check layer permissions based on Keycloak user profile
 *
 * @returns {LayerPermissionsHook} Permission checking methods and admin status
 *
 * @example
 * const { canViewLayer, getAvailableLayers, hasAdminAccess } = useLayerPermissions();
 *
 * // Check if user can view a specific layer
 * if (canViewLayer('Wind Speed')) {
 *   // Render layer
 * }
 *
 * // Get all layers user has permission to view
 * const availableLayers = getAvailableLayers();
 */
export const useLayerPermissions = (): LayerPermissionsHook => {
  const { user, isAdmin } = useContext(AuthContext);

  /**
   * Check if user has permission to view a specific layer
   * Admins can view all layers
   * Regular users can only view layers in their allowedLayers list
   * Note: layerTitle can be either the title or the layerName
   */
  const canViewLayer = useMemo(() => {
    return (layerName: string): boolean => {
      // Admins can see all layers
      if (isAdmin) return true;

      // No user logged in
      if (!user) return false;

      // Camadas marcadas como públicas não passam pelo allowlist: qualquer
      // usuário autenticado as vê, mesmo antes de um admin conceder permissões.
      if (mockLayers.some((l) => l.layerName === layerName && l.publicLayer)) {
        return true;
      }

      // Check for wildcard permission
      if (user.allowedLayers.includes("*")) {
        return true;
      }

      // Find the layer in layersDict layerName
      const layer = mockLayers.find(
        (l) => l.layerName === layerName
      );

      // Check if user's allowedLayers includes this layer's layerName
      return (
        layer !== undefined &&
          user.allowedLayers.includes(layer.layerName)
      );
    };
  }, [user, isAdmin]);

  /**
   * Get all layers that the current user has permission to view
   * Admins get all layers
   * Regular users get filtered list based on their allowedLayers
   */
  const getAvailableLayers = useMemo(() => {
    return (): Layer[] => {
      // Admins can see all layers
      if (isAdmin) return mockLayers;

      // No user logged in - return empty array
      if (!user) return [];

      // If user has wildcard, return all layers
      if (user.allowedLayers.includes("*")) {
        return mockLayers;
      }

      // Filter layers based on user's allowedLayers (check layerName)
      return mockLayers.filter((layer) => {
        return layer.publicLayer || user.allowedLayers.includes(layer.layerName);
      });
    };
  }, [user, isAdmin]);

  return {
    canViewLayer,
    getAvailableLayers,
    hasAdminAccess: isAdmin,
  };
};
